import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

const API_URL = import.meta.env.VITE_API_URL

function Gallery() {
  const { t, i18n } = useTranslation()
  const [paintings, setPaintings] = useState([])
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch(`${API_URL}/paintings`)
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`)
        }
        return res.json()
      })
      .then(data => {
        setPaintings(Array.isArray(data) ? data : [])
        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }, [])

  const safePaintings = Array.isArray(paintings) ? paintings : [];

  if (loading) return (
    <main className="flex-1 w-screen min-h-[400px] flex items-center justify-center bg-gray-100 dark:bg-zinc-900">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 dark:border-gray-100"></div>
    </main>
  )

  if (error) return (
    <main className="flex-1 w-screen min-h-[400px] flex items-center justify-center bg-gray-100 dark:bg-zinc-900">
      <div className="text-center text-red-500 dark:text-red-400 p-8">
        {t('error.loading')}
      </div>
    </main>
  )

  return (
    <div className="min-h-screen w-full bg-gray-100 dark:bg-zinc-900 transition-colors duration-300">
      {/* Gallery Header */}
      <section className="w-screen py-12 px-4 md:px-12 lg:px-24 text-center bg-white dark:bg-zinc-900">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-gray-900 dark:text-white">
            {t('gallery.title')}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            {t('gallery.description')}
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <main className="px-4 md:px-12 lg:px-24 pb-12">
        <div className="max-w-7xl mx-auto">
          {safePaintings.length === 0 ? (
            <div className="text-center p-8">
              <p className="text-gray-500 mb-4 text-lg dark:text-gray-400">{t("no_paintings")}</p>
              <p className="text-sm text-gray-400">{t("add_first_painting")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
              {safePaintings.map((p) => {
                let mainImg = Array.isArray(p.images)
                  ? p.images.find(img => img.is_main === 1 || img.is_main === '1' || img.is_main === true) || p.images[0]
                  : null;
                return (
                <Link
                    to={`/gallery/${p.id}`}
                  className="bg-white/80 dark:bg-zinc-800/80 border rounded-lg shadow-lg overflow-hidden flex flex-col hover:scale-105 hover:shadow-2xl transition-transform duration-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
                  style={{ textDecoration: 'none' }}
                >
                    {mainImg && (
                    <div className="relative group">
                          <img
                            src={`${API_URL}${mainImg.thumb_path || mainImg.file_path}`}
                            alt={p.name}
                            className="w-full h-64 md:h-72 lg:h-80 object-cover group-hover:brightness-90 transition duration-300"
                          />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white text-sm md:text-base">{p.name}</span>
                      </div>
                    </div>
                  )}
                  <div className="p-4 flex-1 flex flex-col">
                    <h3 className="font-bold text-xl md:text-2xl mb-1 text-gray-900 dark:text-gray-100">
                      {p.name}
                    </h3>
                    <div className="flex flex-wrap gap-2 mt-2 items-start">
                      {p.images && p.images.length > 1 && p.images.slice(1, 6).map((img, idx) => (
                        <img
                          key={idx}
                          src={`${API_URL}${img.thumb_path || img.file_path}`}
                          alt={p.name}
                          className="w-12 h-12 md:w-16 md:h-16 object-cover rounded border dark:border-zinc-700"
                        />
                      ))}
                    </div>
                  </div>
                </Link>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

export default Gallery 